import { Suspense } from "react";
import { WelcomeSection } from "@/components/business-summary/WelcomeSection";
import { StorySection } from "@/components/business-summary/StorySection";
import { MissionVisionSection } from "@/components/business-summary/MissionVisionSection";
import { CoreValuesSection } from "@/components/business-summary/CoreValuesSection";
import { BenefitsSection } from "@/components/business-summary/BenefitsSection";
import { ErrorBoundary } from "@/components/ui/error-boundary";
import { useBusinessSummary } from "@/hooks/useBusinessSummary";
import { LoadingState } from "@/components/business-summary/LoadingState";
import { motion } from "framer-motion";
import { toast } from "sonner";

const BusinessSummaryContent = () => {
  const { data: businessSummary, isLoading, error } = useBusinessSummary();

  if (error) {
    toast.error("Failed to load business summary");
    return (
      <div className="text-center py-8">
        <p className="text-red-500">Failed to load business summary. Please try again later.</p>
      </div>
    );
  }

  if (isLoading) return <LoadingState />;

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      className="max-w-4xl mx-auto space-y-12"
    >
      <div className="bg-[#1A1F2C] rounded-xl p-8 shadow-lg">
        <h1 className="text-5xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent mb-6">
          Revolutionizing Hotel Operations Through AI-Powered Innovation
        </h1>
        <p className="text-lg text-gray-300">
          Transforming the hospitality industry with cutting-edge technology and data-driven solutions.
        </p>
      </div>

      <ErrorBoundary>
        <Suspense fallback={<LoadingState />}>
          <WelcomeSection data={businessSummary} />
        </Suspense>
      </ErrorBoundary>

      <ErrorBoundary>
        <Suspense fallback={<LoadingState />}>
          <StorySection data={businessSummary} />
        </Suspense>
      </ErrorBoundary>

      <ErrorBoundary>
        <Suspense fallback={<LoadingState />}>
          <MissionVisionSection data={businessSummary} />
        </Suspense>
      </ErrorBoundary>

      <ErrorBoundary>
        <Suspense fallback={<LoadingState />}>
          <CoreValuesSection />
        </Suspense>
      </ErrorBoundary>

      <ErrorBoundary>
        <Suspense fallback={<LoadingState />}>
          <BenefitsSection />
        </Suspense>
      </ErrorBoundary>
    </motion.div>
  );
};

const BusinessSummary = () => {
  return (
    <ErrorBoundary>
      <Suspense fallback={<LoadingState />}>
        <BusinessSummaryContent />
      </Suspense>
    </ErrorBoundary>
  );
};

export default BusinessSummary;